import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

// Query keys
export const notificationKeys = {
  all: ['notifications'],
  list: () => [...notificationKeys.all, 'list'],
  unreadCount: () => [...notificationKeys.all, 'unread-count'],
  settings: () => [...notificationKeys.all, 'settings'],
};

// API functions
export const notificationApi = {
  // Get all notifications
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  // Delete notification
  deleteNotification: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },

  // Get unread notifications count (optional type filter: 'support_ticket', etc.)
  getUnreadCount: async (params = {}) => {
    const response = await api.get('/notifications/unread-count', { params });
    return response.data;
  },

  // Admin: Create broadcast
  createBroadcast: async (data) => {
    const response = await api.post('/admin/broadcasts', data);
    return response.data;
  },

  // Admin: Get broadcast history
  getBroadcastHistory: async (params = {}) => {
    const response = await api.get('/admin/broadcasts/history', { params });
    return response.data;
  },
};

// React Query hooks
export const useNotifications = (params = {}) => {
  return useQuery({
    queryKey: [...notificationKeys.list(), params],
    queryFn: () => notificationApi.getNotifications(params),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useUnreadNotificationsCount = () => {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await notificationApi.getUnreadCount();
      return response.data?.count || 0;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useUnreadSupportTicketCount = () => {
  return useQuery({
    queryKey: [...notificationKeys.unreadCount(), 'support_ticket'],
    queryFn: async () => {
      const response = await notificationApi.getUnreadCount({ type: 'support_ticket' });
      return response.data?.count || 0;
    },
    refetchInterval: 30000,
  });
};

export const useMarkNotificationAsRead = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationApi.markAsRead,
    onSuccess: (data, notificationId) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      options.onSuccess?.(data, notificationId);
    },
    onError: options.onError,
  });
};

export const useMarkAllNotificationsAsRead = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationApi.markAllAsRead,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      queryClient.invalidateQueries({ queryKey: [...notificationKeys.unreadCount(), 'support_ticket'] });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useDeleteNotification = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationApi.deleteNotification,
    onSuccess: (data, notificationId) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      options.onSuccess?.(data, notificationId);
    },
    onError: options.onError,
  });
};

